import type { ReactNode } from 'react';
import { cn } from '@/lib/core';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { MethodLogos } from './MethodLogos';
import { selectionKey, type CheckoutMethodOption, type CheckoutSelection } from './types';

interface PaymentMethodListProps {
  options: CheckoutMethodOption[];
  selected?: CheckoutSelection;
  onSelect: (selection: CheckoutSelection) => void;
  /** Expanded body for a row, or `null` when the row has nothing to collect (a saved card). */
  renderPanel: (option: CheckoutMethodOption) => ReactNode;
  disabled?: boolean;
}

/**
 * The payment-method picker (Figma: Checkout | Payment methods, node 1716:92982 and its selected
 * variants).
 *
 * One outer hairline box holds every row; the selected row draws its own darker box inside it and
 * its panel opens directly beneath, on the tinted background from the design. Only one row can be
 * open at a time — the radio group is the expansion state, so there is no separate accordion and no
 * way for the open panel and the selected method to disagree.
 *
 * Rows are `h-18` (72px) with the mark on the left and the logo strip on the right, matching the
 * frame geometry in node 1716:92988.
 */
export function PaymentMethodList({
  options,
  selected,
  onSelect,
  renderPanel,
  disabled,
}: PaymentMethodListProps) {
  const selectedKey = selected ? selectionKey(selected) : undefined;

  return (
    <RadioGroup
      value={selectedKey ?? ''}
      onValueChange={value => {
        const match = options.find(option => selectionKey(option.selection) === value);
        if (match) onSelect(match.selection);
      }}
      disabled={disabled}
      className="border-jncquoi-neutral-200 flex flex-col gap-0 border-[0.5px]"
    >
      {options.map(option => {
        const key = selectionKey(option.selection);
        const isSelected = key === selectedKey;
        const panel = isSelected ? renderPanel(option) : null;
        const rowId = `checkout-method-${key}`;

        return (
          <div key={key} className="flex flex-col">
            {/*
              `label` wraps the whole row so the entire 72px band is a hit target, not just the
              20px mark — the design gives no other affordance and a mis-tap here costs a sale.
            */}
            <label
              htmlFor={rowId}
              className={cn(
                'flex h-18 cursor-pointer items-center gap-3 px-4 transition-colors',
                isSelected && 'border-jncquoi-neutral-1000 border-[0.5px]'
              )}
            >
              <RadioGroupItem id={rowId} value={key} disabled={disabled} />
              <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 min-w-0 flex-1 truncate text-[16px] leading-[24px] tracking-[0.25px]">
                {option.label}
              </span>
              <MethodLogos logos={option.logos} />
            </label>

            {/*
              `shrink-0`: the panel holds Stripe iframes that report their height after mount, and a
              flex column would otherwise compress the item to fit, clipping the bottom of the field.
            */}
            {panel && <div className="bg-jncquoi-neutral-50 shrink-0 px-4 py-4">{panel}</div>}
          </div>
        );
      })}
    </RadioGroup>
  );
}
